import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const JobDetailsModal = ({ job, onClose, onApply }) => {
    const [copied, setCopied] = React.useState(false);

    if (!job) return null;

    const formatSalary = (min, max) => {
        return `₹${(min / 100000)?.toFixed(1)}L - ₹${(max / 100000)?.toFixed(1)}L per annum`;
    };

    const handleCopyShareLink = () => {
        const directUrl = `${window.location.origin}/apply/${job?.id}`;
        navigator.clipboard.writeText(directUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-background rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in-up">
                {/* Header */}
                <div className="sticky top-0 bg-background border-b border-border px-6 py-4 flex items-center justify-between z-10">
                    <h2 className="text-xl font-bold text-foreground">Job Details</h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleCopyShareLink}
                            className="px-3 py-1.5 rounded-lg border border-primary/30 bg-primary/10 text-primary text-xs font-semibold flex items-center gap-1.5 hover:bg-primary/20 transition-colors"
                            title="Copy shareable link for candidate"
                        >
                            <Icon name={copied ? "Check" : "Share2"} size={16} />
                            <span>{copied ? "Link Copied!" : "Share Application Link"}</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors accent-foreground"
                        >
                            <Icon name="X" size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6 md:p-8 space-y-8">
                    {/* Job Overview */}
                    <div className="flex flex-col md:flex-row gap-6">
                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                            <Image
                                src={job.companyLogo}
                                alt={job.company}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="flex-1">
                            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{job.title}</h1>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-muted-foreground">
                                <span className="flex items-center gap-1.5">
                                    <Icon name="Building2" size={18} className="text-primary" />
                                    {job.company}
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Icon name="MapPin" size={18} className="text-accent" />
                                    {job.location}
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Icon name="Briefcase" size={18} className="text-success" />
                                    {job.type}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Quick Info Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-muted/30 p-4 rounded-xl border border-border/50">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Experience</p>
                            <p className="font-semibold text-foreground">{job.experience}</p>
                        </div>
                        <div className="bg-muted/30 p-4 rounded-xl border border-border/50">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Salary Range</p>
                            <p className="font-semibold text-foreground">{formatSalary(job.salaryMin, job.salaryMax)}</p>
                        </div>
                        <div className="bg-muted/30 p-4 rounded-xl border border-border/50">
                            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Posted On</p>
                            <p className="font-semibold text-foreground">{new Date(job.postedDate).toLocaleDateString()}</p>
                        </div>
                    </div>

                    {/* Job Description */}
                    <section>
                        <h3 className="text-xl font-bold text-foreground mb-4">Description</h3>
                        <div className="prose prose-sm prose-invert max-w-none text-muted-foreground">
                            <p className="whitespace-pre-wrap leading-relaxed">
                                {job.description}
                            </p>
                        </div>
                    </section>

                    {/* Skills */}
                    <section>
                        <h3 className="text-xl font-bold text-foreground mb-4">Required Skills</h3>
                        <div className="flex flex-wrap gap-2">
                            {job.skills?.map((skill, index) => (
                                <span
                                    key={index}
                                    className="px-4 py-2 rounded-lg bg-primary/10 text-primary font-medium text-sm"
                                >
                                    {skill}
                                </span>
                            ))}
                        </div>
                    </section>

                    {/* Call to Action */}
                    <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-border">
                        {job?.has_applied ? (
                            <button
                                disabled
                                className="flex-1 py-4 font-bold text-base rounded-xl bg-slate-800 text-slate-400 border border-slate-700 cursor-not-allowed flex items-center justify-center gap-2 shadow-inner"
                            >
                                <Icon name="CheckCircle2" size={18} className="text-emerald-500" />
                                <span>Already Applied for this Position</span>
                            </button>
                        ) : (
                            <Button
                                variant="default"
                                size="lg"
                                className="flex-1 py-4 font-bold text-lg"
                                onClick={() => onApply(job)}
                            >
                                Apply for this position
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            size="lg"
                            className="sm:px-8"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default JobDetailsModal;
